import { useCallback, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCreatorAvatarCandidates } from '@/lib/creatorAvatar';

export interface CreatorAvatarImageInput {
  id: string;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
  source_avatar_url?: string | null;
}

export interface CreatorAvatarImageProps {
  creator: CreatorAvatarImageInput;
  /** Visual size. `full` fills the parent (parent must be `relative`). */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Placeholder rendered under the image (and shown if every candidate fails). */
  fallback?: 'icon' | 'initial';
  /** Optional extra class on the outer wrapper (size variants only — ignored for `full`). */
  className?: string;
  /** Optional extra class on the rendered <img> (e.g. blur for visitor gating). */
  imgClassName?: string;
  /** `eager` for above-the-fold lists, `lazy` (default) for everything else. */
  loading?: 'eager' | 'lazy';
}

const SIZE_WRAPPER: Record<Exclude<CreatorAvatarImageProps['size'], undefined>, string> = {
  sm: 'relative w-10 h-10 shrink-0',
  md: 'relative w-12 md:w-14 h-12 md:h-14 shrink-0',
  lg: 'relative w-16 md:w-20 h-16 md:h-20 shrink-0',
  // `full` lets the parent control sizing; we just span its bounds.
  full: 'absolute inset-0',
};

/**
 * Unified creator avatar renderer used across Arsenal grids and dashboards.
 *
 * Rendering rules (do NOT regress — see mem://technical/arsenal/avatar-persistence-protocol):
 *  1. A neutral placeholder is always painted underneath the <img>.
 *  2. The <img> has NO `key={src}` — switching candidates only swaps `src`,
 *     never remounts the element. Eliminates the visible blank/flash flicker.
 *  3. Image fades in (`opacity-0` → `opacity-100`) only after `onLoad`. On
 *     `onError` we reset opacity and try the next candidate silently.
 */
export function CreatorAvatarImage({
  creator,
  size = 'md',
  fallback = 'icon',
  className,
  imgClassName,
  loading = 'lazy',
}: CreatorAvatarImageProps) {
  const candidates = useMemo(
    () =>
      getCreatorAvatarCandidates({
        creatorId: creator.id,
        avatarUrl: creator.avatar_url,
        sourceAvatarUrl: creator.source_avatar_url,
      }),
    [creator.id, creator.avatar_url, creator.source_avatar_url]
  );

  const [index, setIndex] = useState(0);
  const [allFailed, setAllFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleError = useCallback(() => {
    setLoaded(false);
    setIndex((current) => {
      const next = current + 1;
      if (next >= candidates.length) {
        setAllFailed(true);
        return current;
      }
      return next;
    });
  }, [candidates.length]);

  const src = candidates[index];
  const showImage = !allFailed && !!src && candidates.length > 0;
  const isFull = size === 'full';

  // Placeholder content
  const initial = (creator.nickname || creator.username || '?').charAt(0).toUpperCase();
  const placeholder =
    fallback === 'initial' ? (
      <span className="text-lg font-bold text-muted-foreground">{initial}</span>
    ) : (
      <Users className="w-4 md:w-5 h-4 md:h-5 text-muted-foreground" />
    );

  // Placeholder visual: rounded for stand-alone sizes, square-fill for `full` (parent decides shape).
  const placeholderClass = isFull
    ? 'absolute inset-0 w-full h-full bg-secondary flex items-center justify-center'
    : 'absolute inset-0 rounded-full bg-white/[0.04] flex items-center justify-center border-2 border-white/[0.08]';

  const imgBase = isFull
    ? 'absolute inset-0 w-full h-full object-cover'
    : 'absolute inset-0 w-full h-full rounded-full object-cover border-2 border-white/[0.08]';

  return (
    <div className={cn(SIZE_WRAPPER[size], !isFull && className)}>
      <div className={placeholderClass}>{placeholder}</div>
      {showImage && (
        <img
          src={src}
          alt={creator.nickname || creator.username}
          onError={handleError}
          onLoad={() => setLoaded(true)}
          loading={loading}
          decoding="async"
          draggable={false}
          className={cn(
            imgBase,
            'transition-opacity duration-150',
            loaded ? 'opacity-100' : 'opacity-0',
            imgClassName
          )}
        />
      )}
    </div>
  );
}